<template>
  <div>
    <div
      v-if="showTransferCredits"
      class="savings-row mb-space-lg"
      data-testid="transfer-credits-row"
    >
      <div class="savings-row__prompt">
        <h3 class="h3-large mb-space-xs">
          {{ props.content.transferCreditsTitle ?? "Transfer credits" }}
        </h3>
        <div
          class="savings-row__description"
          data-testid="transfer-description"
          role="presentation"
          @click="handleFaqLink($event, 'transfer')"
          v-html="props.content.transferCredotsDescription ?? ''"
        ></div>
      </div>
      <div class="savings-row__control">
        <label class="visually-hidden" for="tuition-calculator-transfer-credits">
          {{ props.content.transferCreditsTitle ?? "Transfer credits" }}
        </label>
        <div
          class="savings-input savings-input--value-box"
          :class="{ 'savings-input--invalid': props.errors.transferCredits }"
          role="presentation"
          @click="focusValueInput"
        >
          <CurrencyInput
            id="tuition-calculator-transfer-credits"
            :model-value="props.modelValue.transferCredits"
            :aria-describedby="transferDescribedBy"
            :aria-invalid="Boolean(props.errors.transferCredits)"
            class="form-control savings-input__field savings-input__field--value-box text-dark-1"
            data-testid="transfer-credits"
            whole-number
            @update:model-value="handleTransferCreditsInput"
            @commit="emit('field-commit', 'transferCredits', $event)"
          />
          <span class="savings-input__suffix" aria-hidden="true">credits</span>
        </div>
      </div>

      <div
        v-if="transferWarningVisible"
        id="tuition-calculator-transfer-credits-warning"
        class="savings-banner"
        data-testid="transfer-warning"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        tabindex="0"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
          data-testid="transfer-warning-icon"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">
          {{ props.transferWarningMessage }}
        </span>
      </div>
      <p
        v-if="props.errors.transferCredits && !transferWarningVisible"
        id="tuition-calculator-transfer-credits-error"
        class="savings-banner"
        data-testid="transfer-error"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        tabindex="0"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ props.errors.transferCredits }}</span>
      </p>
    </div>

    <div v-if="showGrants" class="savings-row mb-space-lg" data-testid="grants-row">
      <div class="savings-row__prompt">
        <h3 class="h3-large mb-space-xs">
          {{ props.content.grantsTitle ?? "Grants" }}
        </h3>
        <div
          class="savings-row__description"
          data-testid="grants-description"
          role="presentation"
          @click="handleFaqLink($event, 'financialAid')"
          v-html="props.content.grantsDescription ?? ''"
        ></div>
      </div>
      <div class="savings-row__control">
        <label class="visually-hidden" for="tuition-calculator-grants">
          {{ props.content.grantsTitle ?? "Grants" }}
        </label>
        <div
          class="savings-input savings-input--value-box"
          :class="{ 'savings-input--invalid': props.errors.grants }"
          role="presentation"
          @click="focusValueInput"
        >
          <span class="savings-input__prefix" aria-hidden="true">$</span>
          <CurrencyInput
            id="tuition-calculator-grants"
            :model-value="props.modelValue.grants"
            :whole-number="true"
            :aria-describedby="fieldDescribedBy('grants')"
            :aria-invalid="Boolean(props.errors.grants)"
            class="form-control savings-input__field savings-input__field--value-box text-dark-1"
            data-testid="grants"
            @update:model-value="handleFinancialAidInput('grants', $event)"
            @commit="emit('field-commit', 'grants', $event)"
          />
        </div>
      </div>

      <div
        v-if="moneyWarningVisible('grants')"
        id="tuition-calculator-grants-warning"
        class="savings-banner"
        data-testid="grants-warning"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ moneyWarningMessage }}</span>
      </div>
      <p
        v-if="props.errors.grants && !moneyWarningVisible('grants')"
        id="tuition-calculator-grants-error"
        class="savings-banner"
        data-testid="grants-error"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ props.errors.grants }}</span>
      </p>
    </div>

    <div class="savings-row mb-space-lg" data-testid="scholarships-row">
      <div class="savings-row__prompt">
        <h3 class="h3-large mb-space-xs">
          {{ props.content.schorlarshipsTitle ?? "Scholarships" }}
        </h3>
        <div
          class="savings-row__description"
          data-testid="scholarships-description"
          role="presentation"
          @click="handleFaqLink($event, 'financialAid')"
          v-html="props.content.schorlarshipDescription ?? ''"
        ></div>
      </div>
      <div class="savings-row__control">
        <label class="visually-hidden" for="tuition-calculator-scholarships">
          {{ props.content.schorlarshipsTitle ?? "Scholarships" }}
        </label>
        <div
          class="savings-input savings-input--value-box"
          :class="{ 'savings-input--invalid': props.errors.scholarships }"
          role="presentation"
          @click="focusValueInput"
        >
          <span class="savings-input__prefix" aria-hidden="true">$</span>
          <CurrencyInput
            id="tuition-calculator-scholarships"
            :model-value="props.modelValue.scholarships"
            :whole-number="true"
            :aria-describedby="fieldDescribedBy('scholarships')"
            :aria-invalid="Boolean(props.errors.scholarships)"
            class="form-control savings-input__field savings-input__field--value-box text-dark-1"
            data-testid="scholarships"
            @update:model-value="handleFinancialAidInput('scholarships', $event)"
            @commit="emit('field-commit', 'scholarships', $event)"
          />
        </div>
      </div>

      <div
        v-if="moneyWarningVisible('scholarships')"
        id="tuition-calculator-scholarships-warning"
        class="savings-banner"
        data-testid="scholarships-warning"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ moneyWarningMessage }}</span>
      </div>
      <p
        v-if="props.errors.scholarships && !moneyWarningVisible('scholarships')"
        id="tuition-calculator-scholarships-error"
        class="savings-banner"
        data-testid="scholarships-error"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ props.errors.scholarships }}</span>
      </p>
    </div>

    <div class="savings-row mb-space-lg" data-testid="employer-benefit-row">
      <div class="savings-row__prompt">
        <h3 class="h3-large mb-space-xs">
          {{ props.content.employerBenefitTitle ?? "Employer tuition benefit" }}
        </h3>
        <div
          class="savings-row__description"
          data-testid="employer-benefit-description"
          v-html="props.content.employerBenefitDescription ?? ''"
        ></div>
      </div>
      <div class="savings-row__control">
        <label class="visually-hidden" for="tuition-calculator-employer-benefit">
          {{ props.content.employerBenefitTitle ?? "Employer tuition benefit" }}
        </label>
        <div
          class="savings-input savings-input--value-box"
          :class="{ 'savings-input--invalid': props.errors.employerTuitionBenefit }"
          role="presentation"
          @click="focusValueInput"
        >
          <span class="savings-input__prefix" aria-hidden="true">$</span>
          <CurrencyInput
            id="tuition-calculator-employer-benefit"
            :model-value="props.modelValue.employerTuitionBenefit"
            :whole-number="true"
            :aria-describedby="fieldDescribedBy('employerTuitionBenefit')"
            :aria-invalid="Boolean(props.errors.employerTuitionBenefit)"
            class="form-control savings-input__field savings-input__field--value-box text-dark-1"
            data-testid="employer-benefit"
            @update:model-value="handleFinancialAidInput('employerTuitionBenefit', $event)"
            @commit="emit('field-commit', 'employerTuitionBenefit', $event)"
          />
        </div>
      </div>

      <div
        v-if="moneyWarningVisible('employerTuitionBenefit')"
        id="tuition-calculator-employer-benefit-warning"
        class="savings-banner"
        data-testid="employer-benefit-warning"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ moneyWarningMessage }}</span>
      </div>
      <p
        v-if="props.errors.employerTuitionBenefit && !moneyWarningVisible('employerTuitionBenefit')"
        id="tuition-calculator-employer-benefit-error"
        class="savings-banner"
        data-testid="employer-benefit-error"
      >
        <svg
          class="savings-banner__icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill="currentColor"
            d="M8 1.33c.32 0 .62.17.79.45l6.67 11.33c.35.6-.08 1.36-.79 1.36H1.33c-.71 0-1.14-.76-.79-1.36L7.21 1.78c.17-.28.47-.45.79-.45Zm.67 9.34H7.33V12h1.34v-1.33Zm0-5.34H7.33v4h1.34v-4Z"
          />
        </svg>
        <span class="savings-banner__message">{{ props.errors.employerTuitionBenefit }}</span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import CurrencyInput from "~/components/what-it-costs-page/cost-calculator-v2/CurrencyInput.vue";
import type { WhatItCosts } from "~/server/resolvers/resolvers-types";
import {
  getMaxFinancialAidAmount,
  getTransferCreditLimit,
  shouldDisplayTransferCredits
} from "~/src/services/TuitionCalculatorFormService";
import type {
  FaqLinkTarget,
  TuitionCalculatorSelections,
  TuitionCalculatorValidationErrors
} from "~/types/tuition-calculator";

type SavingsField = "transferCredits" | "grants" | "scholarships" | "employerTuitionBenefit";
type MoneyField = Exclude<SavingsField, "transferCredits">;

const props = defineProps<{
  modelValue: TuitionCalculatorSelections;
  errors: TuitionCalculatorValidationErrors;
  transferWarningMessage: string;
  exceptionProgramCodes: string[];
  content: Pick<
    Partial<WhatItCosts>,
    | "transferCreditsTitle"
    | "transferCredotsDescription"
    | "grantsTitle"
    | "grantsDescription"
    | "schorlarshipsTitle"
    | "schorlarshipDescription"
    | "employerBenefitTitle"
    | "employerBenefitDescription"
  >;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: TuitionCalculatorSelections): void;
  (event: "field-commit", field: SavingsField, value: number): void;
  (event: "faq-link", target: FaqLinkTarget): void;
}>();

const focusValueInput = (event: MouseEvent): void => {
  const box = event.currentTarget as HTMLElement;
  if (box.contains(document.activeElement)) return;
  box.querySelector("input")?.focus();
};

const handleFaqLink = (event: MouseEvent, target: FaqLinkTarget) => {
  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor) return;
  event.preventDefault();
  emit("faq-link", target);
};

const showGrants = computed(
  () => props.modelValue.degreeType !== "Graduate" && props.modelValue.degreeType !== "Certificates"
);

const showTransferCredits = computed(() => shouldDisplayTransferCredits(props.modelValue));

const transferLimit = getTransferCreditLimit();

const transferWarningVisible = computed(
  () =>
    showTransferCredits.value &&
    !props.modelValue.program?.transferCreditsError &&
    typeof props.modelValue.transferCredits === "number" &&
    props.modelValue.transferCredits > transferLimit
);

const maxFinancialAidAmount = getMaxFinancialAidAmount();
const moneyWarningMessage = "Enter less than $100,000 to continue.";

const moneyWarningVisible = (field: MoneyField): boolean => {
  const value = props.modelValue[field];
  return typeof value === "number" && value > maxFinancialAidAmount;
};

const isFieldWarningVisible = (field: SavingsField): boolean =>
  field === "transferCredits" ? transferWarningVisible.value : moneyWarningVisible(field);

const fieldErrorId = (field: SavingsField): string => {
  const suffix =
    field === "transferCredits"
      ? "transfer-credits"
      : field === "employerTuitionBenefit"
        ? "employer-benefit"
        : field;
  return `tuition-calculator-${suffix}-error`;
};

const fieldWarningId = (field: SavingsField): string => {
  const suffix =
    field === "transferCredits"
      ? "transfer-credits"
      : field === "employerTuitionBenefit"
        ? "employer-benefit"
        : field;
  return `tuition-calculator-${suffix}-warning`;
};

const fieldDescribedBy = (field: SavingsField): string | undefined => {
  const ids: string[] = [];
  const warningVisible = isFieldWarningVisible(field);
  if (props.errors[field] && !warningVisible) {
    ids.push(fieldErrorId(field));
  }
  if (warningVisible) {
    ids.push(fieldWarningId(field));
  }
  return ids.length ? ids.join(" ") : undefined;
};

const transferDescribedBy = computed(() => fieldDescribedBy("transferCredits"));

const updateModel = (updates: Partial<TuitionCalculatorSelections>) => {
  emit("update:modelValue", { ...props.modelValue, ...updates });
};

const handleTransferCreditsInput = (value: number) => {
  updateModel({ transferCredits: value });
};

const handleFinancialAidInput = (field: MoneyField, value: number) => {
  updateModel({ [field]: value });
};

watch(
  () =>
    [
      props.modelValue.degreeType,
      props.modelValue.program?.displayTransferCredits,
      props.modelValue.transferCredits,
      props.modelValue.grants
    ] as const,
  ([degreeType, _displayTransferCredits, transferCredits, grants]) => {
    const updates: Partial<TuitionCalculatorSelections> = {};

    if (!showTransferCredits.value && transferCredits !== 0) {
      updates.transferCredits = 0;
    }

    if ((degreeType === "Graduate" || degreeType === "Certificates") && grants !== 0) {
      updates.grants = 0;
    }

    if (Object.keys(updates).length) {
      updateModel(updates);
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.savings-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--rds-border-color, #d8d8d8);
}

.savings-row__prompt {
  flex: 1 1 24rem;
}

.savings-row__control {
  flex: 0 0 15rem;
  min-width: 0;
  align-self: center;
}

.savings-input {
  position: relative;
  display: flex;
  align-items: center;
}

.savings-input--value-box {
  min-height: 5.25rem;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  border: 1px solid var(--rds-dark-1, #747474);
  background: var(--rds-white, #fff);
  color: var(--rds-dark-1, #747474);
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 2rem;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.075);
}

.savings-input__prefix {
  flex: 0 0 auto;
}

.savings-input__suffix {
  flex: 0 0 auto;
  margin-left: 0.35rem;
}

.savings-input__field {
  min-width: 1ch;
  max-width: 100%;
  height: auto;
}

.savings-input__field--value-box {
  flex: 0 1 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: left;
  box-shadow: none;
}

.savings-input__field--value-box:focus {
  outline: 0;
  box-shadow: none;
}

.savings-input--value-box:focus-within {
  border-color: var(--rds-dark-1, #747474);
  box-shadow: 0 0 0 0.2rem rgb(0 0 0 / 12%);
}

.savings-input--invalid {
  border-color: var(--rds-danger, #b72a2a);
}

.savings-input--invalid:focus-within {
  border-color: var(--rds-danger, #b72a2a);
  box-shadow: 0 0 0 0.2rem rgb(183 42 42 / 15%);
}

.savings-input__field--value-box::-webkit-inner-spin-button,
.savings-input__field--value-box::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.savings-input__field--value-box {
  -moz-appearance: textfield;
  appearance: textfield;
}

@media (max-width: 575.98px) {
  .savings-row__control {
    flex-basis: 100%;
  }

  .savings-input--value-box {
    min-height: 4.75rem;
  }
}
</style>
